import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import axios, { AxiosHeaders } from "axios";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import type { RootState } from "../store";
import { clearUser, setApiLoading, setUser } from "../store/slices/userSlice";
import { API_BASE_URL } from "../utils/constants";
import { toaster, showToast } from "../helper/toaster";
import { jwtDecode } from "jwt-decode";
import { Dispatch } from "redux";
import { useAppSelector } from "../hooks/reduxHooks";
import { useSelector } from "react-redux";

export const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: "" }
  ): BaseQueryFn<{
    url: string;
    method: AxiosRequestConfig["method"];
    data?: AxiosRequestConfig["data"];
    params?: AxiosRequestConfig["params"];
  }> =>
  async ({ url, method, data, params }, { getState, dispatch }) => {
    try {
      const state = getState() as RootState; // Explicitly type getState to RootState
      const { accessToken: token, refreshToken } = state.user;
      const exp = state.user.exp;

      const axiosHeaders = new AxiosHeaders();

      try {
        if (token) {
          const decodedToken = jwtDecode(token);
          dispatch(setUser({ ...state.user, ...decodedToken }));
        }
        if (token && exp) {
          // Check if the token is still valid
          if (Date.now() < exp * 1000) {
            axiosHeaders.set("Authorization", `Bearer ${token}`);
          } else {
            const { status, data } = await axios.post(
              `${API_BASE_URL}/user/refresh-access-token`,
              { refreshToken }
            );
            if (data?.success) {
              dispatch(setUser(data?.data));
            } else {
              dispatch(clearUser());
            }
          }
        }
      } catch (e) {
        console.error("Error decoding token", e);
        dispatch(clearUser()); // Clear user if token can't be decoded
      }

      // Axios interceptors
      axios.interceptors.request.use(
        (config) => {

          if (
            config.url ===
              "http://13.202.74.55:5000/target-achievement/upload" &&
            method === "post"
          ) {
            config.headers["Content-Type"] = "multipart/form-data";
            return config;
          }
          return config;
        },
        (error) => Promise.reject(error)
      );

      axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          const err = error as AxiosError;
          if (err.response?.status === 401) {
            const res = await handleRefreshToken({
              dispatch,
              getState,
              originalRequest: err.config,
            });

            if (res && res?.data.data) {
              return res;
            } else {
              dispatch(clearUser());
            }

            // handleRefreshToken({dispatch, getState, originalRequest: err.config})
            // dispatch(clearUser());
          }

          return Promise.reject(error);
        }
      );

      // Making the API request
      const result = await axios({
        url: baseUrl + url,
        method,
        data,
        params,
        headers: axiosHeaders, // Use the AxiosHeaders object
      });

      return { data: result.data };
    } catch (axiosError) {
      console.log("axiosError", axiosError);
      const err = axiosError as AxiosError<{ message: string }>;

      //@ts-ignore
      const errorMessage: any =
        err.response?.data?.message || err?.message || "Something went wrong!";
      if (typeof errorMessage === "string") {
        showToast(errorMessage, "error");
      }

      return {
        error: {
          status: false,
          data: errorMessage,
        },
      };
    } finally {
      dispatch(setApiLoading(false));
    }
  };

interface HandleRefreshTokenProps {
  dispatch: Dispatch;
  getState: () => any;
  originalRequest: any;
}

const handleRefreshToken = async (
  props: HandleRefreshTokenProps
): Promise<AxiosResponse | false> => {
  const { dispatch, getState, originalRequest } = props;
  const state = getState();
  const refreshToken = state.user.refreshToken;

  if (!refreshToken) {
    return false;
  }

  const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
  });

  try {
    const res = await axiosInstance.post("/user/refresh-access-token", {
      refreshToken,
    });

    if (res && res.data?.success) {
      const authToken = res.data.data.accessToken;

      if (authToken) {
        // dispatch(authActions.updateRefreshTokenData(parsedRes));
        // localStore.setToken(authToken);
        await dispatch(setUser({ ...state.user, accessToken: authToken }));

        if (originalRequest.headers) {
          originalRequest.headers["Authorization"] = `Bearer ${authToken}`;
        } else {
          originalRequest.headers = { Authorization: `Bearer ${authToken}` };
        }

        const originalResponse = await axiosInstance(originalRequest); // Retry the original request
        return originalResponse;
      }
    }

    return false;
  } catch (error) {
    return false;
  }
};
